import { Controller } from '@hotwired/stimulus'
import { addClass, removeClass } from 'utils/toggle_class'

export default class extends Controller {
  static targets = ["menu", "menuButton", "menuItem", "submenu"]

  initialize() {
    this.DELAY_FOR_HOVER = 500
    this.DESKTOP_MIN_WIDTH = "(min-width: 1200px)"
    this.MOBILE_MAX_WIDTH = "(max-width: 1199px)"
  }

  connect() {
    this.setupKeyboardNavigation()
  }

  setupKeyboardNavigation() {
    // Handle keyboard navigation for the entire menu
    this.element.addEventListener('keydown', this.handleMenuKeydown.bind(this))
  }

  handleMenuKeydown(event) {
    // Handle Escape key to close menus
    if (event.key === 'Escape' || event.key === 'Esc') {
      this.handleEscapeKey(event)
      return
    }

    // Only handle arrow keys if we're inside an open menu or submenu
    if (!this.isInsideOpenMenu(event)) return

    // Handle navigation keys
    this.handleNavigationKey(event)
  }

  handleNavigationKey(event) {
    const currentMenu = this.getCurrentMenu(event)
    if (!currentMenu) return

    const focusableItems = this.getFocusableItems(currentMenu)
    const currentIndex = focusableItems.indexOf(event.target)

    switch(event.key) {
      case 'ArrowDown':
        event.preventDefault()
        this.focusNextItem(focusableItems, currentIndex)
        break
      case 'ArrowUp':
        event.preventDefault()
        this.focusPreviousItem(focusableItems, currentIndex)
        break
      case 'Home':
        event.preventDefault()
        focusableItems[0]?.focus()
        break
      case 'End':
        event.preventDefault()
        focusableItems[focusableItems.length - 1]?.focus()
        break
      case 'ArrowRight': {
        this.handleArrowRight(event)
        break
      }
      case 'ArrowLeft': {
        this.handleArrowLeft(event)
        break
      }
    }
  }

  getCurrentMenu(event) {
    return event.target.closest('.c-topnav-menu_menu') ||
           event.target.closest('.c-topnav-menu-item_submenu')
  }

  handleEscapeKey(event) {
    event.preventDefault()
    const openSubmenu = this.submenuTargets.find(submenu =>
      submenu.closest('.c-topnav-menu-item')?.classList.contains('-open')
    )

    if (openSubmenu) {
      // Close submenu and return focus to parent button
      const submenuButton = this.element.querySelector(`[aria-controls="${openSubmenu.id}"]`)
      if (submenuButton) {
        this._closeSubmenu({ target: submenuButton })
        submenuButton.focus()
      }
    } else if (this.element.classList.contains('-open')) {
      // Close main menu and return focus to menu button
      this._closeMenu(event)
      this.menuButtonTarget.focus()
    }
  }

  isInsideOpenMenu(event) {
    const topNavMenu = event.target.closest('.c-topnav-menu')
    const isMainMenuOpen = topNavMenu && topNavMenu.classList.contains('-open')

    const submenuItem = event.target.closest('.c-topnav-menu-item')
    const isSubmenuOpen = submenuItem && submenuItem.classList.contains('-open')

    return isMainMenuOpen || isSubmenuOpen
  }

  handleArrowRight(event) {
    // If on a menu item with submenu, open it
    const triggerElement = event.target.closest('[aria-haspopup]')

    if (triggerElement?.hasAttribute('aria-controls')) {
      const menuItem = triggerElement.closest('[data-topnav-menu-target="menuItem"]')

      // Only open if not already open
      if (menuItem && !menuItem.classList.contains('-open')) {
        event.preventDefault()
        this._openSubmenu({ target: triggerElement })

        // Focus first item in submenu
        const submenuId = triggerElement.getAttribute('aria-controls')
        const submenu = document.getElementById(submenuId)
        const firstItem = submenu && this.getFocusableItems(submenu)[0]
        firstItem?.focus()
      }
    }
  }

  handleArrowLeft(event) {
    // If in a submenu, close it and return focus to parent
    const submenu = event.target.closest('.c-topnav-menu-item_submenu')
    if (submenu) {
      event.preventDefault()
      const parentButton = this.element.querySelector(`[aria-controls="${submenu.id}"]`)
      if (parentButton) {
        this._closeSubmenu({ target: parentButton })
        parentButton.focus()
      }
    }
  }

  getFocusableItems(container) {
    const selector = 'a[href]:not([tabindex="-1"]), button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    return Array.from(container.querySelectorAll(selector)).filter(item => {
      return item.offsetParent !== null // visible items only
    })
  }

  focusNextItem(items, currentIndex) {
    const nextIndex = currentIndex + 1 < items.length ? currentIndex + 1 : 0
    items[nextIndex]?.focus()
  }

  focusPreviousItem(items, currentIndex) {
    const prevIndex = currentIndex - 1 >= 0 ? currentIndex - 1 : items.length - 1
    items[prevIndex]?.focus()
  }

  toggleMenu(event) {
    if (this.element.classList.contains("-open")) {
      this._closeMenu(event)
    } else {
      this._openMenu(event)
    }
  }

  openMenuOnDesktop(event) {
    // Prevents issues on touch-screen devices & only allow mouseover on fullscreen
    if (event.pointerType === "mouse" && window.matchMedia(this.DESKTOP_MIN_WIDTH).matches) {
      this._openMenu(event)
    }
  }

  globalCloseMenu(event) {
    this._closeMenu(event)
  }

  delayedCloseMenu(event) {
    // Prevents mouseout unless fullscreen
    if (window.matchMedia(this.MOBILE_MAX_WIDTH).matches) { return }

    // Delay closing by 0.5 seconds. If the user moves the mouse
    // back into the menu during that time, don't close
    setTimeout(() => {
      if (this.element.matches(':hover') || this.menuTarget.matches(':hover')) { return }

      this._closeMenu(event)
    }, this.DELAY_FOR_HOVER)
  }

  openSubmenuOnDesktop(event) {
    // Prevents issues on touch-screen devices & only allow mouseover on fullscreen
    if (event.pointerType === "mouse" && window.matchMedia(this.DESKTOP_MIN_WIDTH).matches) {
      this._openSubmenu(event)
    }
  }

  toggleSubmenu(event) {
    const menuItem = this._menuItemForEvent(event)

    if (menuItem.classList.contains("-open")) {
      this._closeSubmenu(event)
    } else {
      this._openSubmenu(event)

      // Focus first item in submenu when opening via keyboard
      const trigger = event.target.closest('[aria-haspopup]')
      if (trigger?.hasAttribute('aria-controls')) {
        const submenuId = trigger.getAttribute('aria-controls')
        const submenu = document.getElementById(submenuId)
        const firstItem = submenu && this.getFocusableItems(submenu)[0]
        firstItem?.focus()
      }
    }
  }

  delayedCloseSubmenu(event) {
    // Prevents mouseout unless fullscreen
    if (window.matchMedia(this.MOBILE_MAX_WIDTH).matches) { return }

    const menuItem = this._menuItemForEvent(event)
    const submenu = menuItem.querySelector('[data-topnav-menu-target="submenu"]')

    // Delay closing by 0.5 seconds. If the user moves the mouse
    // back into the submenu during that time, don't close
    setTimeout(() => {
      if (menuItem.matches(':hover') || submenu.matches(':hover')) { return }

      this._closeSubmenu(event)
    }, this.DELAY_FOR_HOVER)
  }

  closeOnClickOutside(event) {
    if (this.element.contains(event.target)) return

    this._closeMenu()
  }

  /// Private ///
  _collapseAllSubmenus() {
    this.menuItemTargets.forEach(menuItem => {
      menuItem.classList.remove("-open")
      menuItem.setAttribute("aria-expanded", false)
      menuItem.querySelector('[aria-haspopup]')?.setAttribute("aria-expanded", false)
    })
  }

  _menuItemForEvent(event) {
    const menuButton = event.target
    return menuButton.closest('[data-topnav-menu-target="menuItem"]')
  }

  _openMenu(_event) {
    document.dispatchEvent(new CustomEvent("topnav.globalCloseMenu"))
    addClass(this.element, "-open")
    this.menuButtonTarget.setAttribute("aria-expanded", true)
  }

  _closeMenu(_event) {
    removeClass(this.element, "-open")
    this.menuButtonTarget.setAttribute("aria-expanded", false)

    this._collapseAllSubmenus()
  }

  _openSubmenu(event) {
    if (this._submenuTouchDelay) { return }

    const menuItem = this._menuItemForEvent(event)

    this._collapseAllSubmenus()
    addClass(menuItem, "-open")
    menuItem.setAttribute("aria-expanded", true)

    // Also update the trigger element's aria-expanded (could be button or div wrapper)
    const trigger = menuItem.querySelector('[aria-haspopup]')
    if (trigger) {
      trigger.setAttribute("aria-expanded", true)
    }
  }

  _closeSubmenu(event) {
    const menuItem = this._menuItemForEvent(event)

    removeClass(menuItem, "-open")
    menuItem.setAttribute("aria-expanded", false)

    // Also update the trigger element's aria-expanded (could be button or div wrapper)
    const trigger = menuItem.querySelector('[aria-haspopup]')
    if (trigger) {
      trigger.setAttribute("aria-expanded", false)
    }
  }
}
